import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface LocalCartItem {
  productType: "watch" | "jewelry" | "investment";
  productId: string;
  quantity: number;
  productDetails: {
    name: string;
    brand?: string;
    model?: string;
    referenceCode?: string;
    image?: string;
    description?: string;
    price?: number;
    weight?: number;
    weightUnit?: string;
    purity?: string;
    category?: string;
    material?: string;
    maxQuantity?: number;
  };
  addedAt: string;
}

interface LocalCartContextType {
  items: LocalCartItem[];
  totalItems: number;
  totalAmount: number;
  addItem: (item: Omit<LocalCartItem, "addedAt">) => void;
  removeItem: (productType: string, productId: string) => void;
  updateQuantity: (productType: string, productId: string, quantity: number) => void;
  clearCart: () => void;
  getCartForSync: () => LocalCartItem[];
  isInCart: (productType: string, productId: string) => boolean;
}

const LocalCartContext = createContext<LocalCartContextType | null>(null);

const LOCAL_CART_KEY = "universo_oro_cart";

export function useLocalCart() {
  const context = useContext(LocalCartContext);
  if (!context) {
    throw new Error("useLocalCart must be used within a LocalCartProvider");
  }
  return context;
}

interface LocalCartProviderProps {
  children: ReactNode;
}

export function LocalCartProvider({ children }: LocalCartProviderProps) {
  const [items, setItems] = useState<LocalCartItem[]>(() => {
    try {
      const stored = localStorage.getItem(LOCAL_CART_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item: Omit<LocalCartItem, "addedAt">) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (i) => i.productType === item.productType && i.productId === item.productId
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        // For watches, always keep quantity at 1 (unique items)
        const maxQty = item.productDetails.maxQuantity || Infinity;
        let newQuantity = item.productType === 'watch' 
          ? 1 
          : updated[existingIndex].quantity + item.quantity;
        // Enforce max quantity limit
        newQuantity = Math.min(newQuantity, maxQty);
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: newQuantity,
          productDetails: {
            ...updated[existingIndex].productDetails,
            maxQuantity: item.productDetails.maxQuantity,
          },
        };
        return updated;
      }

      // For watches, always set quantity to 1
      const maxQty = item.productDetails.maxQuantity || Infinity;
      const quantity = item.productType === 'watch' ? 1 : Math.min(item.quantity, maxQty);
      return [...prev, { ...item, quantity, addedAt: new Date().toISOString() }];
    });
  };

  const removeItem = (productType: string, productId: string) => {
    setItems((prev) =>
      prev.filter((i) => !(i.productType === productType && i.productId === productId))
    );
  };

  const updateQuantity = (productType: string, productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productType, productId);
      return;
    }

    setItems((prev) =>
      prev.map((i) => {
        if (i.productType === productType && i.productId === productId) {
          // Enforce max quantity limit
          const maxQty = i.productDetails.maxQuantity || Infinity;
          const validatedQuantity = Math.min(quantity, maxQty);
          return { ...i, quantity: validatedQuantity };
        }
        return i;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(LOCAL_CART_KEY);
  };

  const getCartForSync = () => items;

  const isInCart = (productType: string, productId: string): boolean => {
    return items.some(
      (item) => item.productType === productType && item.productId === productId
    );
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  
  const totalAmount = items.reduce((sum, item) => {
    const price = item.productDetails.price || 0;
    return sum + price * item.quantity;
  }, 0);

  return (
    <LocalCartContext.Provider
      value={{
        items,
        totalItems,
        totalAmount,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getCartForSync,
        isInCart,
      }}
    >
      {children}
    </LocalCartContext.Provider>
  );
}
